import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { Confirm, Modal } from '@components/common';
import Table from '@components/common/Table';
import UserForm from '@components/ModalComponents/UserForm/UserForm';

import { useQueryRoles } from '@hooks/roles/useQueryRoles';
import { useQueryUserOrganizationsAsPo } from '@hooks/useQueryUserOrganizationsAsPo';
import { useQueryUserStatusesAsPo } from '@hooks/useQueryUserStatusesAsPo';
import { useUsers } from '@hooks/useUsers';

import { auth, users as usersSelector } from '@store/selectors';
import { useAppDispatch, useAppSelector } from '@store/store';

import {
	resetUsersTable,
	setIsEnabled,
	setPage,
	setPageSize,
	setRole,
	setSearchValue,
	setSort,
} from '@slices/users';

import { setFilterUtil } from '@utils/filterUtil';

import { IGroupOperationWithActionDTO } from '@interfaces/common/defaultDTO';
import { TSelectOption } from '@interfaces/components/componentTypes';
import { IUserFormValues } from '@interfaces/components/form';
import {
	ISortBy,
	ITablePagination,
	ITableSearch,
	ITableSort,
	TTableFilter,
} from '@interfaces/components/table';
import { IUserFormDTO, IUserFormOrganisationDTO } from '@interfaces/user/dto/userDTO';
import { IUserEntity } from '@interfaces/user/user';

import { TMenuActionsTypes, TOrganizationType } from '@enums/common';

import { MODAL_WIDTH_480, MODAL_WIDTH_580, modalCloseConfirm } from '@constants/modals';
import { ORGANISATION_PROFILE_PATH_SEGMENT, ORGANISATION_SETTINGS_PATH_SEGMENT } from '@constants/users';
import { statusOptions } from '@components/common/Table/constants';
import { deleteConfirmText } from '@components/UsersTable/constants';

import UserMultipleOrganisationsForm from './UserMultipleOrganisationsForm/UserMultipleOrganisationsForm';
import { getColumns } from './usersColumns';

import styles from './UsersTable.module.scss';

interface IUsersTableProps {
	isCreatable: boolean;
	addTooltipText?: string;
	searchPlaceholder?: string;
	searchTooltipText: string;
	adminCount?: number;
	organisationTypes?: Array<TOrganizationType>;
	maxHeight?: string;
	isWithoutCheckbox?: boolean;
	isRemoveActions?: boolean;
	organisationName?: string;
	isManageUsersTable?: boolean;
}

const UsersTable = ({
	isCreatable,
	addTooltipText = '',
	searchPlaceholder = '',
	searchTooltipText,
	organisationTypes,
	maxHeight,
	isWithoutCheckbox = false,
	isRemoveActions = true,
	organisationName,
	isManageUsersTable = false,
}: IUsersTableProps) => {
	const location = useLocation();

	const {
		accessPermission: {
			platformOwnerAdministrator,
			assetOwnerOrganizationAdministrator,
			networkOperatorOrganizationAdministrator,
			assetConsumerOrganizationAdministrator,
		},
	} = useAppSelector(auth);

	const isOrganisationProfile = location.pathname.includes(ORGANISATION_PROFILE_PATH_SEGMENT);
	const isOrganisationSettings = location.pathname.includes(ORGANISATION_SETTINGS_PATH_SEGMENT);
	const isOrganisationLevelDelete = isOrganisationProfile || isOrganisationSettings;
	const isManageUsersTablePO = platformOwnerAdministrator && isManageUsersTable;

	const isLoggedInUserOrganizationAdministrator =
		assetOwnerOrganizationAdministrator ||
		assetConsumerOrganizationAdministrator ||
		networkOperatorOrganizationAdministrator;

	const isEditAccess = platformOwnerAdministrator
		|| assetOwnerOrganizationAdministrator
		|| networkOperatorOrganizationAdministrator
		|| assetConsumerOrganizationAdministrator;

	const isRemoveAccess = isRemoveActions ? isEditAccess : isRemoveActions;

	const dispatch = useAppDispatch();

	const {
		page,
		pageSize,
		sortDirection,
		sortFieldName,
		searchValue,
		isEnabled,
		role,
		organizationId,
	} = useAppSelector(usersSelector);

	const [checkedIds, setCheckedIds] = useState<Array<string>>([]);
	const [actionId, setActionId] = useState<TMenuActionsTypes>();
	const [editableUser, setEditableUser] = useState<IUserEntity>();
	const [selectedRole, setSelectedRole] = useState<TSelectOption | null>(null);
	const [selectedStatus, setSelectedStatus] = useState<TSelectOption<string> | null>(null);
	const [selectedOrganization, setSelectedOrganization] = useState<TSelectOption<string> | null>(null);
	const [singleIdForRemove, setSingleIdForRemove] = useState<string | null>(null);

	const [isCloseConfirmModal, setIsCloseConfirmModal] = useState(false);
	const [isDirtyFormModal, setIsDirtyFormModal] = useState(false);
	const [isEditMode, setIsEditMode] = useState(false);
	const [isViewMode, setIsViewMode] = useState(false);
	const [isShowDeleteConfirm, setIsShowDeleteConfirm] = useState(false);
	const [isUserFormModal, setIsUserFormModal] = useState(false);
	const [isAdminExistInSelectedItems, setIsAdminExistInSelectedItems] = useState(false);

	const openDeleteConfirm = (isAdminExist?: boolean) => {
		setIsAdminExistInSelectedItems(!!isAdminExist);
		setIsShowDeleteConfirm(prevState => !prevState);
	};

	const closeDeleteConfirm = () => {
		setIsShowDeleteConfirm(false);
		setSingleIdForRemove(null);
	};

	const openUserModal = () => setIsUserFormModal(true);

	const openAddUserModal = () => {
		setIsViewMode(false);
		setIsEditMode(false);
		openUserModal();
	};

	const toggleCloseConfirmModal = () => {
		if (isDirtyFormModal) setIsCloseConfirmModal(prevState => !prevState);
		else closeModals();
	};

	const closeModals = () => {
		setIsUserFormModal(false);
		setIsCloseConfirmModal(false);
		setIsEditMode(false);
		setIsViewMode(false);
		setEditableUser(undefined);
	};

	const {
		users,
		countOfRecords,
		isLoadingTable,
		handleDeleteUsers,
		handleDeleteUser,
		isLoadingDelete,
		handleUpdateUser,
		isLoadingUserForm,
		handleChangeStatusUser,
		handleCreateUser,
		handleDeleteOneUserAsPo,
	} = useUsers({
		page,
		pageSize,
		sortDirection,
		sortFieldName,
		searchValue,
		role,
		organizationId,
		closeDeleteConfirm,
		closeModals,
		isAdminExistInSelectedItems,
		...!isManageUsersTablePO ? { isEnabled } : {},
		...isManageUsersTablePO ? { status: selectedStatus?.value } : {},
		organisationFilterId: selectedOrganization?.value,
		isManageUsersTable,
		isOrganisationLevelDelete,
		isOrganisationProfile,
	});

	const { roles } = useQueryRoles({ organizationId });
	const { userStatusOptions } = useQueryUserStatusesAsPo();
	const { userOrganizationOptions } = useQueryUserOrganizationsAsPo();

	const deleteItemsHandler = useCallback(() => {
		if (singleIdForRemove) {
			return isManageUsersTable
				? handleDeleteOneUserAsPo(singleIdForRemove)
				: handleDeleteUser(singleIdForRemove);
		}

		return handleDeleteUsers({ ids: checkedIds, actionType: actionId });
	}, [
		actionId,
		checkedIds,
		handleDeleteUser,
		handleDeleteUsers,
		singleIdForRemove,
		isManageUsersTable,
		handleDeleteOneUserAsPo,
	]);

	const onSearchHandler = useCallback((value: string) => {
		dispatch(setSearchValue({ searchValue: value }));
	}, [dispatch]);

	const submitForm = useCallback((values: IUserFormValues) => {
		const organizations: IUserFormOrganisationDTO[] = values.organisations?.map((item, index) => {
			return {
				isEnabled: isManageUsersTable
					? item.isEnabled || false
					: true,
				jobTitle: item.jobTitle || '',
				organizationId: item.organizationId,
				roles: values?.organisations?.[index]?.roles
					?.map((role)=> role?.value)
					.filter((role): role is string => !!role) || [],
			};
		}) || [];

		const organisationForEditOrganisationProfile = organizations.find(organisation => {
			return organisation.organizationId === organizationId;
		});

		const submitData: IUserFormDTO = {
			id: values.id,
			organizationId: isOrganisationLevelDelete ? organizationId : undefined,
			values: {
				email: values.email,
				firstName: values.firstName,
				is2FA: values.is2FA,
				jobTitle: isOrganisationProfile && isEditMode
					? organisationForEditOrganisationProfile?.jobTitle
					: values.jobTitle,
				lastName: values.lastName,
				phoneNumber: values.phoneNumber ? values.phoneNumber : undefined,
				roles: isOrganisationSettings
					? values.roles
					: isOrganisationProfile && isEditMode
						? organisationForEditOrganisationProfile?.roles
						: undefined,
				organizations: isOrganisationLevelDelete && isEditMode
					? undefined
					: organizations,
			},
		};

		const submitUserApi = isEditMode || values.id ? handleUpdateUser : handleCreateUser;

		submitUserApi(submitData);
	}, [
		isOrganisationLevelDelete,
		organizationId,
		isOrganisationProfile,
		isEditMode,
		isOrganisationSettings,
		handleUpdateUser,
		handleCreateUser,
		isManageUsersTable,
	]);

	useEffect(() => {
		setFilterUtil({
			selectedValue: selectedStatus,
			currentValue: isEnabled,
			setAction: setIsEnabled,
			fieldName: 'isEnabled',
			dispatch,
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedStatus]);

	useEffect(() => {
		setFilterUtil({
			selectedValue: selectedRole,
			currentValue: role,
			setAction: setRole,
			fieldName: 'role',
			dispatch,
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedRole]);

	useEffect(() => {
		return () => {
			dispatch(resetUsersTable());
		};
	}, [dispatch]);

	const filtersPO: Array<TTableFilter> = useMemo(() => {
		return [
			{
				name: 'status',
				placeholder: 'Status',
				option: selectedStatus,
				options: isManageUsersTable ? userStatusOptions : statusOptions,
				minWidth: '200px',
				setState: setSelectedStatus,
				defaultValue: null,
			},
			{
				name: 'organizationId',
				placeholder: 'Organisation',
				option: selectedOrganization,
				options: userOrganizationOptions,
				minWidth: '200px',
				setState: setSelectedOrganization,
				isHidden: !!organisationName,
				defaultValue: null,
			},
		];
	}, [
		selectedStatus,
		userStatusOptions,
		selectedOrganization,
		userOrganizationOptions,
		organisationName,
		isManageUsersTable,
	]);

	const filtersOrgAdmin: Array<TTableFilter> = useMemo(() => {
		return [
			{
				name: 'status',
				placeholder: 'Status',
				option: selectedStatus,
				options: statusOptions,
				minWidth: '200px',
				setState: setSelectedStatus,
				defaultValue: isEnabled,
			},
		];
	}, [
		isEnabled,
		selectedStatus,
	]);

	const filters: Array<TTableFilter> = useMemo(() => {
		const baseFilters = platformOwnerAdministrator ? filtersPO : filtersOrgAdmin;

		return [
			...baseFilters,
			{
				name: 'role',
				placeholder: 'Role',
				option: selectedRole,
				options: roles,
				minWidth: '250px',
				setState: setSelectedRole,
				isHidden: !organisationName,
				defaultValue: role,
			},
		];
	}, [
		role,
		roles,
		selectedRole,
		filtersOrgAdmin,
		filtersPO,
		platformOwnerAdministrator,
		organisationName,
	]);

	const search: Array<ITableSearch> = useMemo(() => {
		return [
			{
				initialValue: searchValue,
				name: 'searchValue',
				handler: onSearchHandler,
				placeholder: searchPlaceholder,
				tooltipText: searchTooltipText,
			},
		];
	}, [onSearchHandler, searchPlaceholder, searchTooltipText, searchValue]);

	const handleSort = useCallback((sortBy: Array<ISortBy>) => {
		dispatch(setSort({
			sortDirection: !!sortBy[0]?.desc,
			sortFieldName: sortBy[0]?.id,
		}));
	}, [dispatch]);

	const sort: ITableSort = useMemo(() => {
		return {
			initialState: [{ id: sortFieldName, desc: sortDirection }],
			handleSort,
		};
	}, [handleSort, sortDirection, sortFieldName]);

	const pageHandler = useCallback((page: number) => dispatch(setPage({ page })), [dispatch]);

	const pageSizeHandler = useCallback((pageSize: number) => dispatch(setPageSize({ pageSize })), [dispatch]);

	const pagination: ITablePagination = {
		countOfRecords,
		forcePage: page,
		size: pageSize,
		pageHandler,
		pageSizeHandler,
	};

	const columns = useMemo(() => {
		return getColumns({
			openDeleteConfirm,
			setSingleIdForRemove,
			openUserModal,
			setEditableUser,
			handleChangeStatusUser,
			setIsEditMode,
			setIsViewMode,
			viewAsOwner: platformOwnerAdministrator,
			isManageUsersTable,
			isEditAccess,
			isRemoveAccess,
			viewAsOrganizationAdministrator: isLoggedInUserOrganizationAdministrator,
			isOrganisationProfile,
		});
	}, [
		isOrganisationProfile,
		handleChangeStatusUser,
		isEditAccess,
		isRemoveAccess,
		platformOwnerAdministrator,
		isLoggedInUserOrganizationAdministrator,
		isManageUsersTable,
	]);

	const handleCustomDelete = useCallback((data: IGroupOperationWithActionDTO) => {
		openDeleteConfirm();
		setCheckedIds(data.ids);
		setActionId(data.actionType);
	}, []);

	return (
		<div className={styles.usersContainer}>
			<Table
				addTooltipText={addTooltipText}
				columns={columns}
				data={users}
				filters={filters}
				handleCustomDelete={handleCustomDelete}
				isCreatable={isCreatable}
				isEditAccess={isRemoveAccess}
				isLoading={isLoadingTable}
				isPinColumns
				isWithoutCheckbox={isWithoutCheckbox}
				maxHeight={maxHeight}
				pagination={pagination}
				search={search}
				sort={sort}
				stickyLeftColumnIndexes={[0, 1]}
				toggleAddModal={openAddUserModal}
			/>
			<Modal
				isOpen={isUserFormModal}
				onClose={toggleCloseConfirmModal}
				title={isViewMode ? 'View user' : isEditMode ? 'Edit User' : 'Add user'}
				width={MODAL_WIDTH_580}
			>
				{platformOwnerAdministrator
					? (
						<UserMultipleOrganisationsForm
							closeModals={closeModals}
							editableUser={editableUser}
							isEditMode={isEditMode}
							isLoading={isLoadingUserForm}
							isViewMode={isViewMode}
							organisationId={organizationId || undefined}
							organisationName={organisationName}
							setIsDirtyFormModal={setIsDirtyFormModal}
							setIsEditMode={setIsEditMode}
							submitForm={submitForm}
							toggleCloseConfirmModal={toggleCloseConfirmModal}
						/>
					)
					: (
						<UserForm
							closeModals={closeModals}
							editableUser={editableUser}
							isEditMode={isEditMode}
							isLoading={isLoadingUserForm}
							isViewMode={isViewMode}
							organisationTypes={organisationTypes}
							organizationId={organizationId || undefined}
							setIsDirtyFormModal={setIsDirtyFormModal}
							setIsEditMode={setIsEditMode}
							submitForm={submitForm}
							toggleCloseConfirmModal={toggleCloseConfirmModal}
						/>
					)
				}
			</Modal>
			<Modal
				isOpen={isCloseConfirmModal}
				onClose={toggleCloseConfirmModal}
				title='Confirm action'
				width={MODAL_WIDTH_480}
			>
				<Confirm
					onClose={toggleCloseConfirmModal}
					onSubmit={closeModals}
					{...modalCloseConfirm}
				/>
			</Modal>
			<Modal
				isOpen={isShowDeleteConfirm}
				onClose={closeDeleteConfirm}
				title='Confirm action'
				width={MODAL_WIDTH_480}
			>
				<Confirm
					isLoading={isLoadingDelete}
					onClose={closeDeleteConfirm}
					onSubmit={deleteItemsHandler}
					question={deleteConfirmText}
				/>
			</Modal>
		</div>
	);
};

export default UsersTable;
